import { request, ApiError } from './core'
import { logger } from '../../utils/logger'
import type { User } from './types'

export const authApi = {
  login: (email: string, password: string) => {
    const formData = new URLSearchParams()
    formData.append('username', email)
    formData.append('password', password)

    return request<{
      access_token: string
      refresh_token: string
      token_type: string
      user: User
    }>('/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString(),
      retry: false, // Don't retry login requests
    }).then((response) => {
      // Store tokens safely - handle potential storage failures
      try {
        localStorage.setItem('tagp-token', response.access_token)
        if (response.refresh_token) {
          localStorage.setItem('tagp-refresh-token', response.refresh_token)
        }
      } catch (storageError) {
        // Log error but don't fail login - token will be lost on refresh
        logger.error(
          'Failed to store authentication tokens',
          storageError instanceof Error ? storageError : new Error(String(storageError))
        )
      }
      return response;
    }).catch(error => {
      // Intercept 500 errors and provide user-friendly message
      if (error instanceof ApiError && error.status === 500) {
        throw new ApiError(
          'Invalid email or password. If you are a new user, please contact your administrator for registration.',
          error.status,
          error.data
        );
      }
      // Re-throw other errors as-is
      throw error;
    });
  },

  refresh: (refreshToken: string) => {
    if (!refreshToken || refreshToken.trim() === '') {
      return Promise.reject(new ApiError('Refresh token is required', 401))
    }

    return request<{
      access_token: string
      token_type: string
    }>('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
      retry: false, // Don't retry refresh requests
    })
      .then((response) => {
        // Validate response
        if (!response.access_token || typeof response.access_token !== 'string') {
          logger.error('Invalid access token in refresh response', { response })
          throw new ApiError('Invalid access token received from server', 500)
        }

        // Store new access token safely
        try {
          localStorage.setItem('tagp-token', response.access_token)
          logger.debug('Refreshed access token stored successfully')
        } catch (storageError) {
          logger.error(
            'Failed to store refreshed token',
            storageError instanceof Error ? storageError : new Error(String(storageError))
          )
          // Don't throw - token refresh succeeded, storage failure is recoverable
        }
        return response
      })
      .catch((error) => {
        // If refresh fails, clear tokens
        if (error instanceof ApiError && error.status === 401) {
          try {
            localStorage.removeItem('tagp-token')
            localStorage.removeItem('tagp-refresh-token')
          } catch (storageError) {
            logger.error('Failed to clear tokens after refresh failure', storageError)
          }
          window.dispatchEvent(new CustomEvent('auth:token-expired'))
        }
        throw error
      })
  },

  logout: () => {
    return request('/auth/logout', {
      method: 'POST',
      retry: false,
    }).finally(() => {
      // Always remove tokens safely
      try {
        localStorage.removeItem('tagp-token')
        localStorage.removeItem('tagp-refresh-token')
      } catch (storageError) {
        logger.error('Failed to clear tokens in logout', storageError)
      }
    })
  },

  me: () => request<User>('/auth/me'),

  /**
   * Authoritative RBAC verdict for one feature + action, straight from the
   * evaluator the services enforce with.
   *
   * `useCanAccess().canAccess` cannot answer this: it was reduced to a stub that
   * returns true for every feature once loading settles, because UI gating moved
   * to the `view_actions` eye toggle. That leaves action permissions (edit /
   * create / delete) with no client-side answer at all, so a surface that must
   * not offer an action it cannot perform has to ask the backend.
   *
   * `apiRoute` (canonical "POST /api/..." form) opts into child-scoped
   * enforcement, where an explicit subfeature deny overrides a parent allow.
   */
  checkAccess: (feature: string, action: string, apiRoute?: string) => {
    const params = new URLSearchParams({ feature, action })
    if (apiRoute) params.set('api_route', apiRoute)
    return request<{ allowed: boolean }>(`/auth/check-access?${params.toString()}`)
  },

  /**
   * Get quota usage for the current user's tenant.
   * This endpoint is available to all authenticated users (not just Platform Admins).
   *
   * @returns Quota usage information for the user's tenant
   */
  getMyQuotaUsage: () =>
    request<{
      tenant_id: string | null
      quota_usage: Record<
        string,
        { current: number; limit: number; available: number; percent: number; unlimited: boolean }
      >
    }>('/auth/me/quotas'),

  /**
   * Handle OAuth callback response (for Azure AD and Google OAuth)
   * Stores both access and refresh tokens
   */
  handleOAuthCallback: (response: {
    access_token: string
    refresh_token: string
    token_type: string
    user: User
  }) => {
    // Store tokens safely - handle potential storage failures
    try {
      localStorage.setItem('tagp-token', response.access_token)
      if (response.refresh_token) {
        localStorage.setItem('tagp-refresh-token', response.refresh_token)
      }
    } catch (storageError) {
      logger.error(
        'Failed to store OAuth tokens',
        storageError instanceof Error ? storageError : new Error(String(storageError))
      )
      throw storageError
    }
    return response
  },
}
